import React, { useEffect, useState } from 'react'
import { useAppDispatch, useAppState } from 'store'
import { useParams } from 'react-router'
import Button from 'components/Button/Button'
import { Input } from 'components/Input'
import CheckBox from 'components/CheckBox/CheckBox'
import CalendarInput from 'components/CalendarInput'
import Calendar from 'components/Calendar/Calendar'
import { appointmentActions } from 'store/slices/appointments/appointments.slice'
import { transactionActions } from 'store/slices/transactions/transactions.slice'
import {
  EditProgramContainer,
  EditProgramTitle,
  EditProgramCourtContainer,
  EditProgramDateContainer,
  EditProgramButtonContainer,
  EditProgramHeaderContainer,
  EditProgramCheckboxContainer,
  EditProgramCourtCheckTitle,
  EditProgramCourtCheckTitleM,
  LeftProgramButtonContainer,
  SubmitProgramButtonContainer,
  CalendarContainer,
  EditProgramCalendarContainer,
  RightProgramButtonContainer,
} from './EditProgramForm.styled'
import {
  EditProgramFormBody,
  EditProgramFormProps,
  ProgramTimes,
} from './EditProgramForm.types'
import { Court, Location } from 'models/location'
import PageLoader from 'components/PageLoader/PageLoader'
import {
  chunckArrayInGroups,
  orderByNumParamAscending,
} from '../../../../utils/index'
import { random, remove } from 'lodash'
import moment from 'moment'
import FacilityFormTemplate from 'templates/FacilityFormTemplate'
import cogoToast from 'cogo-toast'
import { programActions } from 'store/slices/programs'
import Select from 'components/Select'
import { EventCalendar } from 'types/event-calendar-types'
import ArrowLeft from 'icons/ArrowLeft'
import { DragAndDropCalendarProps } from 'components/Calendar/Calendar.props'
import { SlotInfoProps } from 'pages/Reservation/ReservationForm.props'
import { findIndex } from 'lodash'

const initialForm: EditProgramFormBody = {
  name: '',
  price: undefined,
  number_of_courts: 0,
  preferred_courts: [],
  capacity: undefined,
  start_date: '',
  end_date: '',
  program_times: [],
  quickbooks_item_id: null,
  individual_sessions: true,
}

const DayWeekNames = [
  {
    id: '1',
    title: 'Monday',
  },
  {
    id: '2',
    title: 'Tuesday',
  },
  {
    id: '3',
    title: 'Wednesday',
  },
  {
    id: '4',
    title: 'Thursday',
  },
  {
    id: '5',
    title: 'Friday',
  },
  {
    id: '6',
    title: 'Saturday',
  },
  {
    id: '7',
    title: 'Sunday',
  },
]

const EditProgramForm = ({ onBack, type }: EditProgramFormProps) => {
  const params = useParams<Location>()
  const dispatch = useAppDispatch()
  const { location, loading } = useAppState(state => state.appointments)
  const { quickbooksDropdown, loadingQuickbooks } = useAppState(
    state => state.transaction
  )

  const [form, setForm] = useState<EditProgramFormBody>(initialForm)
  const [courtSelectedList, setCourtSelectedList] = useState<any>({})
  const [dropdownLoading, setDropdownLoading] = useState<boolean>(true)

  const [submiting, setSubmiting] = useState<boolean>(false)
  const [goNext, setGoNext] = useState<boolean>(false)

  const [events, setEvents] = useState<EventCalendar[]>([])

  useEffect(() => {
    dispatch(appointmentActions.getLocation(params ? params.locationId : ''))
  }, [])

  useEffect(() => {
    dispatch(transactionActions.getQuickbooks(params ? params.locationId : ''))
  }, [])

  useEffect(() => {
    if (loadingQuickbooks === 'loading') {
      setDropdownLoading(true)
    }
    if (loadingQuickbooks === 'succeeded') {
      setDropdownLoading(false)
    }
  }, [loadingQuickbooks])

  const onChange = (key: string, value: any) => {
    if (key === 'price') value = Number(value)
    if (key === 'capacity') value = Number(value)
    if (key === 'quickbooks_item_id')
      value = value && value.value ? Number(value.value) : null
    if (key === 'court') {
      const courtArr = courtSelected(value as number)
      setForm(form => ({
        ...form,
        preferred_courts: courtArr,
        number_of_courts: courtArr.length,
      }))

      courtSelectedList[value] = !courtSelectedList[value]
      setCourtSelectedList(courtSelectedList)

      return
    }
    setForm(form => ({
      ...form,
      [key]: value,
    }))
  }

  const courtSelected = (value: number) => {
    let auxPreferredCourts = form?.preferred_courts || []
    if (auxPreferredCourts.includes(value)) {
      auxPreferredCourts = remove(auxPreferredCourts, e => e === value)
    } else {
      auxPreferredCourts.push(value)
    }
    return auxPreferredCourts
  }

  const Courts = (courts: Court[]) => {
    const courtsOrderById = orderByNumParamAscending(courts, 'id')
    const courtsGroups = chunckArrayInGroups(courtsOrderById, 3) as Court[][]

    if (Object.keys(courtSelectedList).length === 0) {
      const courtList: any = {}
      courtsGroups.map(courtGroup =>
        courtGroup.map(court => (courtList[court.id.toString()] = false))
      )
      setCourtSelectedList(courtList)
    }

    return courtsGroups.map((courtGroup, i) => (
      <EditProgramCheckboxContainer key={i}>
        {courtGroup.map(court => (
          <CheckBox
            weight="500"
            text={court.name}
            value={`${court.id}`}
            key={court.id}
            onChange={e => onChange('court', court.id)}
            checked={courtSelectedList[`${court.id}`]}
          />
        ))}
      </EditProgramCheckboxContainer>
    ))
  }

  const goToProgram = () => {
    setGoNext(false)
    window.scrollTo(0, 0)
  }

  const goToProgramCalendar = () => {
    let error = false
    Object.entries(form).forEach(field => {
      if (
        field[0] != 'program_times' &&
        field[0] != 'quickbooks_item_id' &&
        Object.keys(initialForm).includes(field[0]) &&
        (field[1] === null || field[1] === undefined || field[1].length === 0)
      ) {
        error = true
      }
    })

    if (error) {
      cogoToast.error('All fields are required.', {
        position: 'top-right',
      })
      return
    }
    window.scrollTo(0, 0)
    setGoNext(true)
  }

  const handleOnSelectedEvent = async (calendarEvent: SlotInfoProps) => {
    const newEvent: EventCalendar = {
      id: `${calendarEvent.resourceId}-${random(1000)}`,
      title: 'Program Slot',
      start: new Date(calendarEvent.start),
      end: new Date(calendarEvent.end),
      color: 'secondaryDisabled',
      textColor: 'black',
      resourceId: calendarEvent.resourceId,
    }
    setEvents([...events, newEvent])
  }

  const handleResize = (currentEvent: DragAndDropCalendarProps) => {
    const { event, start, end } = currentEvent
    const nextEvents = events.map((existingEvent: EventCalendar) => {
      return existingEvent.id === event.id
        ? { ...existingEvent, start, end }
        : existingEvent
    })
    setEvents(nextEvents)
  }

  const handleDrop = (currentEvent: DragAndDropCalendarProps) => {
    const { event, start, end, resourceId } = currentEvent
    if (event && events.length > 0) {
      const idx: number = findIndex(events, { id: event.id })
      const updatedEvent = { ...event, start, end, resourceId }
      const nextEvents = [...events]
      nextEvents.splice(idx, 1, updatedEvent)
      setEvents(nextEvents)
    }
  }

  const getProgramTimes = () => {
    const programTimes: ProgramTimes[] = []
    events.map(event => {
      programTimes.push({
        start_time: moment(event.start).format('YYYY-MM-DD[T]HH:mm:ss[Z]'),
        end_time: moment(event.end).format('YYYY-MM-DD[T]HH:mm:ss[Z]'),
        day_of_week: Number(event.resourceId),
      })
    })
    return programTimes
  }

  const onSubmit = async () => {
    setSubmiting(true)
    if (events.length === 0) {
      cogoToast.error('Program slots are required.', {
        position: 'top-right',
      })
      setSubmiting(false)
      return
    }
    form.price = form.price ? Math.round(100 * form.price) : undefined
    form.program_times = getProgramTimes()

    await dispatch(
      programActions.createProgram({
        locationId: params ? params.locationId : '',
        body: form,
      })
    ).then((res: any) => {
      setSubmiting(false)
      if (res.payload.error === false) {
        onBack()
      }
    })
  }

  return (
    <>
      <FacilityFormTemplate>
        {loading === 'loading' && !location ? (
          <PageLoader />
        ) : !goNext ? (
          <EditProgramContainer>
            <EditProgramHeaderContainer>
              <EditProgramTitle>
                {type === 'create' ? 'Create' : 'Edit'} program
              </EditProgramTitle>
              <LeftProgramButtonContainer>
                <Button
                  LeftIcon={ArrowLeft}
                  text="Back to Programs"
                  variant="primary"
                  preset="ghost"
                  size={'medium'}
                  onClick={onBack}
                />
              </LeftProgramButtonContainer>
            </EditProgramHeaderContainer>
            <Input
              label={'Name'}
              placeholder=""
              onTextChange={e => onChange('name', e)}
              value={form.name}
              required
            />
            <Input
              label={'Price'}
              placeholder=""
              type={'number'}
              onTextChange={e => onChange('price', e)}
              value={form.price}
              required
            />
            <EditProgramCourtContainer>
              <EditProgramCourtCheckTitle>
                Courts
                <EditProgramCourtCheckTitleM>*</EditProgramCourtCheckTitleM>
              </EditProgramCourtCheckTitle>
              {location && Courts(location?.courts)}
            </EditProgramCourtContainer>

            <div style={{ marginBottom: 30 }}>
              <Input
                label={'Capacity'}
                placeholder=""
                type={'number'}
                onTextChange={e => onChange('capacity', e)}
                value={form.capacity}
                required
                style={{ width: '35.5%' }}
              />
            </div>

            <CheckBox
              weight="500"
              text="Allow individual registrations"
              value="allowIndividualRegistration"
              checked={form.individual_sessions}
              disabled
            />

            <EditProgramDateContainer>
              <CalendarInput
                label={'Start Date'}
                onDateSelected={e => {
                  onChange('start_date', moment(e).format('YYYY-MM-DD'))
                }}
                value={form.start_date ? new Date(form.start_date) : undefined}
                required
              />

              <CalendarInput
                label={'End Date'}
                onDateSelected={e => {
                  onChange('end_date', moment(e).format('YYYY-MM-DD'))
                }}
                value={form.end_date ? new Date(form.end_date) : undefined}
                required
              />
            </EditProgramDateContainer>

            <div style={{ width: '85%' }}>
              <Select
                autocomplete
                label="Accounting Category"
                options={quickbooksDropdown}
                placeholder="Filled State"
                isLoading={dropdownLoading}
                onChange={e => {
                  onChange('quickbooks_item_id', e)
                }}
                value={quickbooksDropdown.find(
                  option => option.value === form.quickbooks_item_id?.toString()
                )}
              />
            </div>

            <EditProgramButtonContainer>
              <Button
                text="Next"
                variant="primary"
                preset="filled"
                size={'medium'}
                onClick={goToProgramCalendar}
                disabled={goNext}
                style={{ width: '200px' }}
              />
            </EditProgramButtonContainer>
          </EditProgramContainer>
        ) : (
          <EditProgramCalendarContainer>
            <RightProgramButtonContainer>
              <Button
                LeftIcon={ArrowLeft}
                text="Back"
                variant="primary"
                preset="ghost"
                size={'medium'}
                onClick={goToProgram}
              />
            </RightProgramButtonContainer>
            <LeftProgramButtonContainer>
              <Button
                text="Clear"
                variant="neutral"
                preset="filled"
                size={'medium'}
                onClick={() => setEvents([])}
              />
            </LeftProgramButtonContainer>
            <CalendarContainer>
              <Calendar
                selectable
                draggable={true}
                events={events}
                toolbar={false}
                showDoubleTime={true}
                defaultView={'day'}
                columns={DayWeekNames}
                onSelected={e => handleOnSelectedEvent(e as SlotInfoProps)}
                onResize={handleResize}
                onEventDrop={handleDrop}
              />
            </CalendarContainer>
            <SubmitProgramButtonContainer>
              <Button
                text={'Submit'}
                variant="primary"
                preset="filled"
                size={'medium'}
                onClick={onSubmit}
                disabled={submiting}
                style={{ width: '200px' }}
              />
            </SubmitProgramButtonContainer>
          </EditProgramCalendarContainer>
        )}
      </FacilityFormTemplate>
    </>
  )
}

export default EditProgramForm
